import { getAuthToken } from './auth/tokenStorage';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { API_URL } from '../config';

import { createLogger } from '../utils/logger';

const logger = createLogger('AccessGrants');
export interface LockAccessRequest {
  taskKey: string;
  entityId: string;
}

export interface UnlockAccessRequest {
  taskKey: string;
  entityId: string;
  granteeEmails: string[];
}

export interface AccessControlResponse {
  success: boolean;
  message?: string;
  grantedTo?: string[];
  error?: string;
}

class AccessGrantsService {
  private async getAuthToken(): Promise<string | null> {
    return await getAuthToken();
  }

  private async makeRequest(endpoint: string, options: RequestInit = {}): Promise<Response> {
    const token = await this.getAuthToken();

    const response = await fetch(`${API_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
        ...options.headers,
      },
    });

    return response;
  }

  /**
   * Lock access - only user can see until death
   */
  async lockAccess(data: LockAccessRequest): Promise<AccessControlResponse> {
    try {
      const response = await this.makeRequest('/api/unified-access/lock', {
        method: 'POST',
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to lock access: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      logger.error('Error locking access:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to lock access',
      };
    }
  }

  /**
   * Unlock access - grant immediate access to selected users
   */
  async unlockAccess(data: UnlockAccessRequest): Promise<AccessControlResponse> {
    try {
      const response = await this.makeRequest('/api/unified-access/unlock', {
        method: 'POST',
        body: JSON.stringify(data),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to unlock access: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      logger.error('Error unlocking access:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to unlock access',
      };
    }
  }

  /**
   * Get all access grants for a specific task/entity
   */
  async getTaskAccess(taskKey: string, entityId: string): Promise<any> {
    try {
      const response = await this.makeRequest(`/api/unified-access/task/${taskKey}/${entityId}`, {
        method: 'GET',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to get task access: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      logger.error('Error getting task access:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to get task access',
      };
    }
  }
}

export const accessGrantsService = new AccessGrantsService();